import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useForm, SubmitHandler } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  Stack,
  Typography,
  Button,
  useTheme,
  CircularProgress,
} from "@mui/material";
import dayjs from "dayjs";

import {
  FormProvider,
  RHFTextField,
  RHFCheckbox,
  RHFDatePicker,
} from "@app/components";
import { Strings } from "@app/constants";
import { RootState } from "@app/redux/root-reducer";
import { brand } from "@app/modules/brand/slice";
import { model } from "@app/modules/model/slice";
import { productFindById, updateProductById } from "../slice";
import { IAddProduct } from "../types";
import { UploadImages } from "./uploadImages";
import { renderDropDown, renderFieldsHeading } from "./render-drop-down";
import ExtraInformationFields from "./extraInfo";

export const EditProduct = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { productId } = location.state as { productId: number };
  const [loading, setLoading] = useState(true);
  const [selectedFrontViewImage, setSelectedFrontViewImage] = useState<
    File | string | null
  >(null);
  const [selectedBackViewImage, setSelectedBackViewImage] = useState<
    File | string | null
  >(null);
  const [selectedLeftSideViewImage, setSelectedLeftSideViewImage] = useState<
    File | string | null
  >(null);
  const [selectedRightSideViewImage, setSelectedRightSideViewImage] = useState<
    File | string | null
  >(null);
  const [selectedInteriorDashboardImage, setSelectedInteriorDashboardImage] =
    useState<File | string | null>(null);
  const [selectedOdometerReadingImage, setSelectedOdometerReadingImage] =
    useState<File | string | null>(null);

  const { productData, brandData, modelData } = useSelector(
    (state: RootState) => ({
      productData: state.allProductReducer.productFindById.data,
      brandData: state.brand.brand.data,
      modelData: state.modelReducer.model.data,
    }),
  );
  const methods = useForm<IAddProduct>({
    defaultValues: {},
  });

  useEffect(() => {
    dispatch(brand());
    dispatch(model());
    if (productId) {
      dispatch(productFindById({ productId: Number(productId) }));
    }
  }, [dispatch, productId]);

  console.log(productData, "pr0-d");

  useEffect(() => {
    if (productData?.car) {
      methods.reset({
        brandId: productData.car.brand?.id || "",
        modelId: productData.car.model?.id || "",
        manufacturingYear: productData.car.manufacturingYear || "",
        transmission: productData.car.transmission || "",
        engineType: productData.car.engineType || "",
        driven: productData.car.driven || "",
        variant: productData.car.variant || "",
        Price: productData.car.price || 0,
        color: productData.car.color || "",
        description: productData.car.description,
        insuranceAvailable: productData.car.insuranceAvailable ? "Yes" : "No",
        insuranceType: productData.car.insuranceType || "",
        expiryDate: productData.car.insuranceValidTill
          ? dayjs(productData.car.insuranceValidTill)
          : null,
        anyClaimsMade: productData.car.anyClaimsMade ? "Yes" : "No",
        usageType: productData.car.usageType || "",
        rcAvailable: productData.car.rcAvailable ? "Yes" : "No",
        loanHyonthecation: productData?.car.loanHypothecation ? "Yes" : "No",
        accidentHistory: productData.car.accidentHistory || "",
        fullServiceRecord: productData.car.serviceHistory ? "Yes" : "No",
        carCondition: productData.car.carCondition || "",
        isAvailableForSell: productData.car.carAvailableForSell || false,
        frontView: productData.car.images?.[0]?.url || null,
        backView: productData.car.images?.[1]?.url || null,
        leftSideView: productData.car.images?.[2]?.url || null,
        rightSideView: productData.car.images?.[3]?.url || null,
        interiorDashboard: productData.car.images?.[4]?.url || null,
        odometerReading: productData.car.images?.[5]?.url || null,
        extraInfo: productData.car.extraInformation || [],
      });

      setSelectedFrontViewImage(productData.car.images?.[0]?.url || null);
      setSelectedBackViewImage(productData.car.images?.[1]?.url || null);
      setSelectedLeftSideViewImage(productData.car.images?.[2]?.url || null);
      setSelectedRightSideViewImage(productData.car.images?.[3]?.url || null);
      setSelectedInteriorDashboardImage(
        productData.car.images?.[4]?.url || null,
      );
      setSelectedOdometerReadingImage(productData.car.images?.[5]?.url || null);

      setLoading(false);
    }
  }, [productData, methods]);

  // Variant dropdown logic
  const selectedModelId = methods.watch("modelId");
  const selectedModel = modelData?.find(
    (m: any) => m.id === Number(selectedModelId),
  );
  const variantsForModel = selectedModel?.variant?.[0]
    ? selectedModel.variant[0].split(",")
    : [];

  useEffect(() => {
    if (variantsForModel.length === 1) {
      methods.setValue("variant", variantsForModel[0]);
    }
  }, [selectedModelId, variantsForModel, methods]);

  const onSubmit: SubmitHandler<IAddProduct> = (form) => {
    const formData = new FormData();
    const keyMap: Record<string, string> = {
      loanHyonthecation: "loanHypothecation",
      fullServiceRecord: "serviceHistory",
      isAvailableForSell: "carAvailableForSell",
      Price: "price",
      expiryDate: "insuranceValidTill",
      extraInfo: "extraInformation",
    };

    const imageKeys = [
      "frontView",
      "backView",
      "leftSideView",
      "rightSideView",
      "interiorDashboard",
      "odometerReading",
    ];

    Object.keys(form).forEach((key) => {
      // Skip image fields here; they'll be appended as `images` below if they're File objects
      if (imageKeys.includes(key)) return;

      const backendKey = keyMap[key] || key;
      let value: any = (form as any)[key];

      if (
        ["insuranceAvailable", "anyClaimsMade", "rcAvailable"].includes(
          backendKey,
        )
      ) {
        value = value === "Yes";
      }
      if (backendKey === "carAvailableForSell") value = !!value;
      if (backendKey === "insuranceValidTill" && value)
        value = dayjs(value).format("YYYY-MM-DD");
      if (typeof value === "number") value = value.toString();
      if (backendKey === "specifications") value = JSON.stringify(value || []);

      if (backendKey === "extraInformation" && Array.isArray(value)) {
        value.forEach((item: any, index: number) => {
          formData.append(
            `extraInformation[${index}][heading]`,
            item.heading || "",
          );
          formData.append(
            `extraInformation[${index}][description]`,
            item.description || "",
          );
        });
        return;
      }

      if (value !== undefined && value !== null)
        formData.append(backendKey, value);
    });

    const imagesToAppend = [
      form.frontView,
      form.backView,
      form.leftSideView,
      form.rightSideView,
      form.interiorDashboard,
      form.odometerReading,
    ];

    imagesToAppend.forEach((img) => {
      if (img instanceof File) {
        formData.append("images", img);
      } else if (typeof img === "string" && img) {
        formData.append("images", img);
      }
    });

    try {
      const entries: Array<[string, any]> = [];
      for (const pair of (formData as any).entries()) {
        entries.push(pair as [string, any]);
      }
      console.log("FormData entries for update:", entries);
    } catch (e) {
      console.log("Unable to read FormData entries for logging", e);
    }

    dispatch(
      updateProductById({
        id: productId,
        formValues: formData as unknown as IAddProduct,
      }),
    );
    navigate(-1);
  };

  if (loading) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{ minHeight: "50vh" }}
      >
        <CircularProgress />
        <Typography mt={2}>Loading data...</Typography>
      </Stack>
    );
  }

  return (
    <Stack
      sx={{
        px: 2,
        pt: 0,
        pb: 5,
        bgcolor: theme.palette.common.white,
        borderRadius: 2,
      }}
    >
      <FormProvider methods={methods}>
        {renderFieldsHeading(Strings.product.editCarDetails)}

        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
        >
          {renderDropDown(Strings.product.brand, brandData)}
          {renderDropDown(Strings.product.manufacturingYear)}
        </Stack>

        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
        >
          {renderDropDown(Strings.product.model, modelData)}
          {renderDropDown(Strings.product.engineType)}
        </Stack>

        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
        >
          {renderDropDown(Strings.product.transmissionType)}
          {renderDropDown(
            Strings.product.variant,
            variantsForModel.map((v: string, i: number) => ({
              id: i,
              name: v,
            })),
          )}
        </Stack>

        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
        >
          {renderDropDown(Strings.product.carTraveled)}
          <Stack
            sx={{
              width: { xs: "100%", md: "48%" },
              mb: 2,
              mt: { xs: 2, md: 0 },
            }}
          >
            <Typography sx={{ mb: 1, fontWeight: 600, color: "#222" }}>
              Price
            </Typography>
            <RHFTextField name="Price" placeholder="Add Price" type="number" />
          </Stack>
        </Stack>

        <Stack sx={{ width: { xs: "100%", md: "48%" }, mb: 2, mt: 2 }}>
          <Typography sx={{ mb: 1, fontWeight: 600, color: "#222" }}>
            Color
          </Typography>
          <RHFTextField name="color" placeholder="Add Color" />
        </Stack>

        <Stack sx={{ width: "100%", mb: 2, mt: 2 }}>
          <Typography sx={{ mb: 1, fontWeight: 600, color: "#222" }}>
            Description
          </Typography>
          <RHFTextField
            name="description"
            placeholder="Add description..."
            multiline
            rows={3}
          />
        </Stack>

        {/* Insurance */}
        {renderFieldsHeading(Strings.product.insurance)}
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
        >
          {renderDropDown(Strings.product.insuranceAvailable)}
          {renderDropDown(Strings.product.insuranceType)}
        </Stack>

        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
        >
          <Stack sx={{ width: { xs: "100%", md: "48%" }, mb: 2, mt: 2 }}>
            <Typography sx={{ mb: 1, fontWeight: 600, color: "#222" }}>
              Expiry Date
            </Typography>
            <RHFDatePicker name="expiryDate" label="Expiry Date" />
          </Stack>
          {renderDropDown(Strings.product.anyClaimsMade)}
        </Stack>

        {/* Ownership & RC */}
        {renderFieldsHeading(Strings.product.ownershipAndRC)}
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
        >
          {renderDropDown(Strings.product.usageType)}
          {renderDropDown(Strings.product.rcAvailable)}
        </Stack>
        {renderDropDown(Strings.product.loanHyonthecation)}

        {/* Accident & Service */}
        {renderFieldsHeading(Strings.product.accidentAndServiceHistory)}
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
        >
          {renderDropDown(Strings.product.accidentHistory)}
          {renderDropDown(Strings.product.fullServiceRecord)}
        </Stack>

        {/* Car Condition */}
        {renderFieldsHeading(Strings.product.carCondition)}
        {renderDropDown(Strings.product.carCondition)}

        <RHFCheckbox
          label={Strings.product.isAvailableForSell || "Is Available For Sell"}
          name="isAvailableForSell"
        />

        {/* Extra Info */}
        <ExtraInformationFields label="Extra Information" />

        {/* Images */}
        {renderFieldsHeading(Strings.product.productImage)}
        <UploadImages
          selectedFrontViewImage={selectedFrontViewImage}
          setSelectedFrontViewImage={(file: File | string | null) => {
            setSelectedFrontViewImage(file);
            methods.setValue("frontView", file);
          }}
          selectedBackViewImage={selectedBackViewImage}
          setSelectedBackViewImage={(file: File | string | null) => {
            setSelectedBackViewImage(file);
            methods.setValue("backView", file);
          }}
          selectedLeftSideViewImage={selectedLeftSideViewImage}
          setSelectedLeftSideViewImage={(file: File | string | null) => {
            setSelectedLeftSideViewImage(file);
            methods.setValue("leftSideView", file);
          }}
          selectedRightSideViewImage={selectedRightSideViewImage}
          setSelectedRightSideViewImage={(file: File | string | null) => {
            setSelectedRightSideViewImage(file);
            methods.setValue("rightSideView", file);
          }}
          selectedInteriorDashboardImage={selectedInteriorDashboardImage}
          setSelectedInteriorDashboardImage={(file: File | string | null) => {
            setSelectedInteriorDashboardImage(file);
            methods.setValue("interiorDashboard", file);
          }}
          selectedOdometerReadingImage={selectedOdometerReadingImage}
          setSelectedOdometerReadingImage={(file: File | string | null) => {
            setSelectedOdometerReadingImage(file);
            methods.setValue("odometerReading", file);
          }}
        />

        {/* Buttons */}
        <Stack
          sx={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "end",
            gap: 2,
            mt: 4,
          }}
        >
          <Button
            sx={{ width: { xs: "40%", sm: 250 } }}
            variant="outlined"
            onClick={() => navigate(-1)}
          >
            {Strings.button.cancel}
          </Button>
          <Button
            sx={{
              width: { xs: "40%", sm: 250 },
              color: theme.palette.common.white,
            }}
            variant="contained"
            onClick={methods.handleSubmit(onSubmit)}
            disabled={loading}
          >
            {Strings.button.save}
          </Button>
        </Stack>
      </FormProvider>
    </Stack>
  );
};
